import { api } from '@/lib/api'

/** @typedef {import('@/types/api').Appointment} Appointment */
/** @typedef {import('@/types/api').AppointmentSlotConfig} AppointmentSlotConfig */

/**
 * @param {{ from: string, to: string, stylist_id?: number, source?: string, q?: string }} params
 * @returns {Promise<Appointment[]>}
 */
export async function fetchAppointments(params) {
  const { data } = await api.get('/appointments', { params })
  return data.data.appointments
}

/**
 * @returns {Promise<AppointmentSlotConfig>}
 */
export async function fetchAppointmentSlotConfig() {
  const { data } = await api.get('/appointments/slot-config')
  return data.data.slot_config
}

/**
 * @param {number} appointmentId
 * @param {{ starts_at: string, stylist_id?: number | null, chair_id?: number | null }} payload
 * @returns {Promise<Appointment>}
 */
export async function rescheduleAppointment(appointmentId, payload) {
  const { data } = await api.post(`/appointments/${appointmentId}/reschedule`, payload)
  return data.data.appointment
}

/**
 * @param {number} appointmentId
 * @returns {Promise<Appointment>}
 */
export async function approveOnlineAppointment(appointmentId) {
  const { data } = await api.post(`/appointments/${appointmentId}/approve-online`)
  return data.data.appointment
}

/**
 * @typedef {{ value: string, label: string }} AppointmentSourceOption
 */

/**
 * @returns {Promise<AppointmentSourceOption[]>}
 */
export async function fetchAppointmentSources() {
  const { data } = await api.get('/appointments/sources')
  return data.data.sources
}

/**
 * @param {Record<string, unknown>} payload
 * @returns {Promise<{ appointments: Appointment[], recurringGroupId?: string | null }>}
 */
export async function createAppointment(payload) {
  const { data } = await api.post('/appointments', payload)

  if (Array.isArray(data.data.appointments)) {
    return {
      appointments: data.data.appointments,
      recurringGroupId: data.data.recurring_group_id ?? null,
    }
  }

  return {
    appointments: [data.data.appointment],
    recurringGroupId: null,
  }
}

/**
 * @param {number} appointmentId
 * @param {{ chair_id?: number }} [payload]
 * @returns {Promise<Appointment>}
 */
export async function checkInAppointment(appointmentId, payload = {}) {
  const { data } = await api.post(`/appointments/${appointmentId}/check-in`, payload)
  return data.data
}

/**
 * @param {number} appointmentId
 * @returns {Promise<Appointment>}
 */
export async function startAppointmentInChair(appointmentId) {
  const { data } = await api.post(`/appointments/${appointmentId}/start-in-chair`)
  return data.data.appointment
}

/**
 * @param {'cancel' | 'no_show'} context
 * @returns {Promise<Array<{ value: string, label: string }>>}
 */
export async function fetchCancellationReasons(context) {
  const { data } = await api.get('/appointments/cancellation-reasons', {
    params: { context },
  })
  return data.data.reasons
}

/**
 * @param {number} appointmentId
 * @param {{ cancellation_reason_code: string }} payload
 * @returns {Promise<Appointment>}
 */
export async function cancelAppointment(appointmentId, payload) {
  const { data } = await api.post(`/appointments/${appointmentId}/cancel`, payload)
  return data.data.appointment
}

/**
 * @param {number} appointmentId
 * @param {{ cancellation_reason_code: string }} payload
 * @returns {Promise<Appointment>}
 */
export async function markAppointmentNoShow(appointmentId, payload) {
  const { data } = await api.post(`/appointments/${appointmentId}/no-show`, payload)
  return data.data.appointment
}

/**
 * @param {number} appointmentId
 * @returns {Promise<Appointment>}
 */
export async function triggerStylistReady(appointmentId) {
  const { data } = await api.post(`/appointments/${appointmentId}/stylist-ready`)
  return data.data.appointment
}

/**
 * @param {number} appointmentId
 * @param {{ stylist_id: number, message?: string }} payload
 */
export async function notifyAppointmentStylist(appointmentId, payload) {
  const { data } = await api.post(`/appointments/${appointmentId}/notify-stylist`, payload)
  return data
}
